import React, { useState } from 'react';
import { chartColor } from '../chartTheme';

/** One rounded bar on a full-width track. */
export interface PillDatum {
  key: string;
  label: string;
  /** Fill of the track, 0 to 1. */
  fill: number;
  /** Shown above the bar: a percentage, or `n of m` for small populations. */
  valueLabel: string;
  /** Exact values on hover. */
  detail: string;
  /** Option thumbnail for image choice questions (data, not decoration). */
  image?: string;
  /** A grouped remainder: a value row with no bar. */
  summary?: boolean;
}

/** Props for {@link PillBars}. */
export interface PillBarsProps {
  data: PillDatum[];
  /** Bars shown before `+N more`; the rest expand in place. */
  limit?: number;
  /** Extra hidden options counted in `+N more` (for example a grouped remainder). */
  hiddenExtra?: number;
  /** Opens the respondents behind a bar. Without it bars are focusable but inert. */
  onSelect?: (datum: PillDatum) => void;
}

/**
 * Friendly horizontal bars: each option's label and percentage above a rounded
 * bar filled to its share on a soft track. One colour; bars start at zero.
 */
export const PillBars: React.FC<PillBarsProps> = ({ data, limit, hiddenExtra = 0, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const bars = data.filter(datum => !datum.summary);
  const visible = limit && !expanded ? bars.slice(0, limit) : data;
  const hidden = limit && !expanded ? bars.length - visible.length + hiddenExtra : 0;
  return (
    <div data-chart="pills" className="flex flex-col gap-3">
      <ul className="flex flex-col gap-3">
        {visible.map(datum => {
          const name = `${datum.label}: ${datum.valueLabel}`;
          const content = (
            <>
              <span className="flex items-baseline justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-foreground">
                  {datum.image && <img src={datum.image} alt="" className="h-6 w-6 shrink-0 rounded-md object-cover" />}
                  <span className="truncate">{datum.label}</span>
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{datum.valueLabel}</span>
              </span>
              {!datum.summary && (
                <span className="block h-3 w-full overflow-hidden rounded-full" style={{ background: chartColor.track }}>
                  <span data-mark className="block h-full rounded-full" style={{ width: `${Math.round(Math.min(1, Math.max(0, datum.fill)) * 100)}%`, background: chartColor.mark }} />
                </span>
              )}
            </>
          );
          const className = 'flex w-full flex-col gap-1.5 rounded-xl text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring';
          return (
            <li key={datum.key}>
              {onSelect && !datum.summary ? (
                <button type="button" aria-label={name} data-tip={datum.label} data-tip-detail={datum.detail} className={`${className} cursor-pointer`} onClick={() => onSelect(datum)}>
                  {content}
                </button>
              ) : (
                <div role="img" aria-label={name} data-tip={datum.label} data-tip-detail={datum.detail} tabIndex={0} className={className}>{content}</div>
              )}
            </li>
          );
        })}
      </ul>
      {hidden > 0 && (
        <button type="button" onClick={() => setExpanded(true)}
          className="self-start rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring">
          {`+${hidden} more`}
        </button>
      )}
    </div>
  );
};
